import clsx from 'clsx';
import { Button } from '../../../Components/Button/Button';
import { Filter } from '../../../Components/Filter/Filter';
import { Icons } from '../../../Components/Icons/Icons';
import { useLoader } from '../../../contexts/LoaderContext';
import { useAlert } from '../../../contexts/AlertContext';
import { resources } from '../../../api/resources';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../../api/users';

const typeOptions = [
  { value: 'link', label: 'Link' },
  { value: 'file', label: 'File' },
];

interface ResourcesHeaderProps {
  agencyFilter: string;
  typeFilter: string;
  categoryFilter: string;
  onAgencyFilterChange: (value: string) => void;
  onTypeFilterChange: (value: string) => void;
  onCategoryFilterChange: (value: string) => void;
  selectedResources: string[];
  onAddResource: () => void;
  agencyOptions: Array<{ value: string; label: string }>;
  categoryOptions: Array<{ value: string; label: string }>;
  onDeleteMultipleResources: (ids: number[]) => void;
  setSelected: React.Dispatch<React.SetStateAction<number[]>>;
  hideDownloadAllButton?: boolean;
}

export const ResourcesHeader = ({
  agencyFilter,
  typeFilter,
  categoryFilter,
  onAgencyFilterChange,
  onTypeFilterChange,
  onCategoryFilterChange,
  selectedResources,
  onAddResource,
  agencyOptions,
  categoryOptions,
  onDeleteMultipleResources,
  setSelected,
  hideDownloadAllButton,
}: ResourcesHeaderProps) => {
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const { userData } = useAuth();
  const isShowAddNewResourceButton =
    userData?.role === UserRole.ADMIN || userData?.role === UserRole.DIRECTOR;

  const handleDownloadAll = async () => {
    if (selectedResources.length === 0) return;
    try {
      showLoader();
      const blob = await resources.downloadMultiple(selectedResources.map(Number));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resources-batch-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showAlert('Download started successfully', 'success');
    } catch (error) {
      showAlert('Failed to download resources', 'error');
    } finally {
      hideLoader();
    }
  };

  const handleDeleteAll = () => {
    if (selectedResources.length === 0) return;
    onDeleteMultipleResources(selectedResources.map(Number));
  };

  return (
    <>
      <h1>
        Resources
        <a
          className="btn outline"
          target="_blank"
          href="https://app.smartsheet.com/b/form/2a6e5672f2ab4db9993ad10ef2d0d9bb"
          rel="noreferrer"
        >
          Submit a Request
        </a>
      </h1>
      <div className="page-header">
        <Filter
          hidden={false}
          filters={[
            {
              options: agencyOptions,
              value: agencyFilter,
              onChange: onAgencyFilterChange,
              placeholder: 'Agency',
            },
            {
              options: typeOptions,
              value: typeFilter,
              onChange: onTypeFilterChange,
              placeholder: 'Type',
            },
            {
              options: categoryOptions,
              value: categoryFilter,
              onChange: onCategoryFilterChange,
              placeholder: 'Category',
            },
          ]}
        />
        <div className="btns">
          {!hideDownloadAllButton && selectedResources.length > 0 && (
            <div className="download-all show" onClick={handleDownloadAll}>
              {Icons.Download} Download All
            </div>
          )}
          {isShowAddNewResourceButton && selectedResources.length > 0 && (
            <div
              className={clsx('delete-all', {
                show: selectedResources.length > 0,
              })}
              onClick={handleDeleteAll}
            >
              {Icons.Delete} Delete All
            </div>
          )}
          {isShowAddNewResourceButton && (
            <Button variant="button" onClick={onAddResource}>
              {Icons.WhitePlus} Add New Resource
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
